import { apiPaths } from '@core/http/apiPaths';
import { httpClient } from '@core/http/httpClient';
import { EntityReducers } from '@core/store/EntityReducers';
import { getPathPayload, formatDateForApi } from '@hwm/ui-lib';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { defaultValues } from '@features/LoggerProgramming/defaultValues';
import { DaysOfWeek } from '@models/DaysOfWeek';
import { ILoggerProgram, ILoggerProgramResponse, ILoggerProgramRequest } from '@models/ILoggerProgram';

const getProgram = createAsyncThunk(
  `${EntityReducers.loggerPrograms}/getProgram`,
  async (payload: getPathPayload<typeof apiPaths.loggers.getProgram>) => {
    const response: AxiosResponse<ILoggerProgramResponse> = await httpClient.get(
      `${apiPaths.loggers.getProgram(payload)}`
    );

    const result: ILoggerProgram = {
      ...response.data,
      startTime: response.data.startTime ? new Date(response.data.startTime) : null,
      soundRecordingMode:
        response.data.soundRecordingMode !== undefined ? response.data.soundRecordingMode : 'NoRecordings',
      daysToRecord: response.data.daysToRecord
        ? {
            monday: response.data.daysToRecord.find((x) => x === 'Monday') !== undefined,
            tuesday: response.data.daysToRecord.find((x) => x === 'Tuesday') !== undefined,
            wednesday: response.data.daysToRecord.find((x) => x === 'Wednesday') !== undefined,
            thursday: response.data.daysToRecord.find((x) => x === 'Thursday') !== undefined,
            friday: response.data.daysToRecord.find((x) => x === 'Friday') !== undefined,
            saturday: response.data.daysToRecord.find((x) => x === 'Saturday') !== undefined,
            sunday: response.data.daysToRecord.find((x) => x === 'Sunday') !== undefined,
          }
        : new DaysOfWeek(),
      id: payload.loggerId,
      minutesBeetweenRecordings: response.data.minutesBeetweenRecordings ?? defaultValues.minutesBeetweenRecordings,
      numberOfSoundRecordings: response.data.numberOfSoundRecordings ?? defaultValues.numberOfSoundRecordings,
      lenghtOfRecording: response.data.lenghtOfRecording ?? defaultValues.lenghtOfRecording,
      repeatRecordingsForDays: response.data.repeatRecordingsForDays ?? defaultValues.repeatRecordingsForDays,
      whenInLeakRepeatRecordingsForDays: response.data.whenInLeakRepeatRecordingsForDays
        ? response.data.whenInLeakRepeatRecordingsForDays
        : response.data.whenInLeakMode === null
          ? defaultValues.whenInLeakRepeatRecordingsForDays
          : 0,
    };

    return result;
  }
);

export interface ISaveProgramParams extends getPathPayload<typeof apiPaths.loggers.saveProgram> {
  body: ILoggerProgramRequest;
}

interface IBulkProgramChangeParams extends ILoggerProgramRequest {
  accountIds?: number[];
  namedListId?: number;
}

interface IBulkProgramChange extends ILoggerProgramRequest {
  siteIds?: number[];
  accountIds?: number[];
}

const saveProgram = createAsyncThunk(
  `${EntityReducers.loggerPrograms}/saveProgram`,
  async ({ body, loggerId }: ISaveProgramParams) => {
    const request: ILoggerProgramRequest = body;

    const response: AxiosResponse<ILoggerProgram> = await httpClient.put(
      `${apiPaths.loggers.saveProgram({ loggerId })}`,
      request
    );

    return {
      ...response.data,
      id: loggerId,
    };
  }
);

const bulkSaveProgram = createAsyncThunk(
  `${EntityReducers.loggerPrograms}/bulkSaveProgram`,
  async (body: IBulkProgramChangeParams) => {
    const request: IBulkProgramChange = {
      ...body,
      accountIds: body?.accountIds ?? null,
    };

    const response: AxiosResponse<any> = await httpClient.post(`${apiPaths.sites.loggerProgramChange()}`, request);

    return response.data;
  }
);

export const mapLoggerProgramToRequest = (program: Partial<ILoggerProgram>): ILoggerProgramRequest => ({
  minutesBeetweenRecordings: program.minutesBeetweenRecordings,
  numberOfSoundRecordings: program.numberOfSoundRecordings,
  lenghtOfRecording: program.lenghtOfRecording,
  repeatRecordingsForDays: program.repeatRecordingsForDays,
  soundRecordingMode: program.soundRecordingMode,
  startTime: formatDateForApi(program.startTime),
  daysToRecord: [
    program.daysToRecord.monday ? 'Monday' : null,
    program.daysToRecord.tuesday ? 'Tuesday' : null,
    program.daysToRecord.wednesday ? 'Wednesday' : null,
    program.daysToRecord.thursday ? 'Thursday' : null,
    program.daysToRecord.friday ? 'Friday' : null,
    program.daysToRecord.saturday ? 'Saturday' : null,
    program.daysToRecord.sunday ? 'Sunday' : null,
  ].filter((x) => x !== null),
  whenInLeakRepeatRecordingsForDays: program.whenInLeakRepeatRecordingsForDays,
  whenInLeakMode:
    program.soundRecordingMode === 'NoRecordings'
      ? null
      : program.whenInLeakRepeatRecordingsForDays === 8
        ? 'RecordOnGivenDays'
        : 'RecordEveryNumberOfDays',
});

export const thunks = { getProgram, saveProgram, bulkSaveProgram };
